import { BooleanInput } from '@angular/cdk/coercion';
import { DOCUMENT } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    effect,
    Inject,
    Input,
    signal,
    ViewEncapsulation
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { CasdoorAuthService } from 'app/core/auth/casdoor-auth.service';
import { Capacitor } from '@capacitor/core';
import { AuthInitializationService } from 'app/core/auth/auth-initialization.service';
import { UserService } from 'app/core/user/user.service';
import { UserProfileService } from 'app/modules/dashboard/dipita/user-profile/user-profile.service';
import { PatientService } from 'app/modules/dashboard/dipita/requester/patient/patient.service';
import { PatientCacheService } from 'app/modules/dashboard/dipita/requester/patient/patient-cache.services';
import { RequestService } from 'app/modules/dashboard/dipita/requester/request/request.service';
import { ExpertRequestService } from 'app/modules/dashboard/dipita/expert/shared/services/expert-request.service';
import { ApltFileService } from 'app/modules/dashboard/file-manager/file-manager.service';
import { Constants } from '../../../../utils/constants';

@Component({
    selector: 'user',
    templateUrl: './user.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'user',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        MatDividerModule,
    ],
})
export class UserComponent {
    /* eslint-disable @typescript-eslint/naming-convention */
    static ngAcceptInputType_showAvatar: BooleanInput;
    /* eslint-enable @typescript-eslint/naming-convention */

    @Input() showAvatar: boolean = true;
    
    // Service signals
    user = this._userService.user;

    /** Profile photo (data URL) shown in the header; null falls back to the user icon. */
    avatarUrl = signal<string | null>(null);
    private _loadedPictureId: string | null = null;

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _userService: UserService,
        private _userProfileService: UserProfileService,
        private _patientService: PatientService,
        private _patientCacheService: PatientCacheService,
        private _requestService: RequestService,
        private _expertRequestService: ExpertRequestService,
        private auth: CasdoorAuthService,
        private authInitService: AuthInitializationService,
        private _fileService: ApltFileService,
        @Inject(DOCUMENT) public document: Document,
    ) {
        // The photo lives on the clinician profile (a file id), not on /api/me: load it whenever
        // the profile's picture changes - after sign-in, or after an upload on the profile page.
        effect(() => {
            const pictureId = this._userProfileService.clinicianProfile()?.profilePicture ?? null;
            if (pictureId === this._loadedPictureId) return;
            this._loadedPictureId = pictureId;
            if (!pictureId) {
                this.avatarUrl.set(null);
                return;
            }
            this._fileService.getFileDataUrl(pictureId)
                .then((url) => {
                    if (this._loadedPictureId === pictureId) this.avatarUrl.set(url);
                })
                .catch(() => this.avatarUrl.set(null));
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    goToProfile(): void {
        this._router.navigate(['/dashboard/profile']);
    }

    installPlugin(): void {
        window.open(Constants.plugin_url,  '_blank');
    }


    private getLogoutReturnUrl(): string {
        if (Capacitor.isNativePlatform()) {
            // For native app, use app scheme deep link without environment-specific host
            return `com.dipitatouch.app://sign-in`;
        }
        // For web, use current origin
        return `${this.document.location.origin}/#/sign-in`;
    }

    /**
     * Sign out
     */
    signOut(): void {
        const returnUrl = this.getLogoutReturnUrl();
        console.log('Logging out with returnTo:', returnUrl);

        // Clear all cached user data before logout
        this._userService.clearUser();
        this._userProfileService.clearProfileData();
        this._patientService.clearData();
        this._patientCacheService.clearCache();
        this._requestService.clearData();
        this._expertRequestService.clearData();

        // Reset auth initialization state
        this.authInitService.reset();


        this.auth.logout({
            logoutParams: {
                returnTo: returnUrl
            }
        });
    }
}